import {
  AfterViewChecked,
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  Input,
  OnDestroy,
  Optional
} from '@angular/core';
import { LayoutComponent } from '@shared/layout/layout.component';
import { ScrollContainerDirective } from './scroll-container.directive';

/**
 * Shows/Hides the element this directive is on depending if the user scrolls to
 * a determined point.
 */
@Directive({
  selector: '[mfpVisibilityOnScroll]'
})
export class VisibilityOnScrollDirective implements AfterViewInit, AfterViewChecked, OnDestroy {
  /**
   * The point (in pixels) to scroll to before the show/hide logic is being applied.
   */
  @Input() scrollPoint?: number;
  /**
   * The element which its position is checked to scroll to before the show/hide logic is being applied.
   */
  @Input() scrollElement?: HTMLElement;

  @Input() action: 'show' | 'hide' = 'show';
  protected elementPreviousDisplay = 'block';

  private scrollEventSet = false;

  private get scrollContainerEl(): HTMLElement {
    return (
      this.scrollContainerDirective?.containerEl ||
      this.layoutComponent?.scrollContainerDirective?.containerEl ||
      ((window as unknown) as HTMLElement)
    );
  }

  // FIXME: can't take the ScrollContainerDirective directly from LayoutComponent
  // since it is declared inside the LayoutComponent itself and is black-boxed to
  // child elements. Was trying to figure a way to expose that black-boxed directive
  // on the LayoutComponent 'providers' array but with no success.
  constructor(
    private el: ElementRef,
    @Host() @Optional() private layoutComponent: LayoutComponent,
    @Optional() private scrollContainerDirective: ScrollContainerDirective
  ) {}

  ngAfterViewInit(): void {
    this.elementPreviousDisplay = this.el.nativeElement.style.display;
    if (this.layoutComponent) {
      // We are inside a LayoutComponent so we should be sure that we set the
      // scroll event listener correctly
    } else {
      this.checkScrollPoint();
      this.scrollContainerEl.addEventListener('scroll', this.checkScrollPoint);
      this.scrollEventSet = true;
    }
  }

  ngAfterViewChecked(): void {
    if (this.layoutComponent && !this.scrollEventSet) {
      if (this.layoutComponent.scrollContainerDirective) {
        this.checkScrollPoint();
        // scrollContainerEl should be 'this.layoutComponent.scrollContainerDirective' here
        this.scrollContainerEl.addEventListener('scroll', this.checkScrollPoint);
        this.scrollEventSet = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.scrollContainerEl.removeEventListener('scroll', this.checkScrollPoint);
  }

  private checkScrollPoint = () => {
    let posToCheck = this.scrollPoint || 0;
    if (this.scrollElement) {
      posToCheck = this.getTopOffset(this.scrollElement);
    }
    if (this.scrollContainerEl.scrollTop >= posToCheck) {
      this.onScrollThresholdPassed();
    } else {
      this.onScrollThresholdNotPassedYet();
    }
  };

  private onScrollThresholdPassed(): void {
    if (this.action === 'show') {
      this.el.nativeElement.style.display = this.elementPreviousDisplay;
    } else if (this.action === 'hide') {
      this.el.nativeElement.style.display = 'none';
    }
  }

  private onScrollThresholdNotPassedYet(): void {
    const productManagerFooterHeight = this.el.nativeElement.style.height;
    if (
      this.scrollContainerEl.scrollHeight - productManagerFooterHeight - this.scrollContainerEl.scrollTop <=
      this.scrollContainerEl.clientHeight
    ) {
      this.onScrollThresholdPassed();
    } else {
      if (this.action === 'show') {
        this.el.nativeElement.style.display = 'none';
      } else if (this.action === 'hide') {
        this.el.nativeElement.style.display = this.elementPreviousDisplay;
      }
    }
  }

  private getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 50;
    const controlElTop = controlEl.getBoundingClientRect().top;

    if (this.layoutComponent) {
      const containerTop = this.scrollContainerEl.getBoundingClientRect().top;
      const absoluteControlElTop = controlElTop + this.scrollContainerEl.scrollTop;
      return absoluteControlElTop - containerTop - labelOffset;
    } else {
      const absoluteControlElTop = controlElTop + window.scrollY;
      return absoluteControlElTop - labelOffset;
    }
  }
}
